import type { UserPresenceState } from '@annoteer/sync-layer';

/**
 * Collapse a list of awareness CONNECTIONS into the list of other PEOPLE.
 *
 * Awareness counts connections, and connections outnumber people in two
 * ordinary situations:
 *
 * - A presenter who shares to the meeting stage runs two instances of this app
 *   at once, their side panel and the stage, each with its own awareness entry.
 * - A client that goes away without closing its socket — a phone leaving for
 *   the Android file picker, say — lingers until awareness times it out.
 *
 * So a two-person meeting read "3 viewers · NT MC MC" on 2026-08-28: the
 * presenter's own stage counted as a stranger, and one participant was counted
 * twice.
 *
 * Clients from older builds report no `userId`; they keep their per-connection
 * identity rather than being merged with each other on a guess.
 *
 * @param remoteUsers every remote awareness state (local client already excluded)
 * @param localUserId the viewing user, so their own second surface is dropped
 */
export function dedupeViewers(
  remoteUsers: UserPresenceState[],
  localUserId?: string,
): UserPresenceState[] {
  const byPerson = new Map<string, UserPresenceState>();
  for (const u of remoteUsers) {
    if (localUserId && u.userId === localUserId) continue;
    const key = u.userId ?? `client:${u.clientId}`;
    const existing = byPerson.get(key);
    // Prefer their side panel: it is where they are working, so its page is
    // the one worth showing on the avatar.
    if (!existing || (existing.surface === 'stage' && u.surface !== 'stage')) {
      byPerson.set(key, u);
    }
  }
  return Array.from(byPerson.values());
}

/**
 * One pointer per PERSON, for the cursor overlay.
 *
 * Same problem as `dedupeViewers` and the same keying, but a different tie
 * break. A presenter sharing to the stage runs two instances of this app side
 * by side on one screen, so moving the mouse across the window publishes a
 * pointer from BOTH, and everyone else saw two arrows carrying the same name —
 * reported from a phone on 2026-09-22. One human has one pointer.
 *
 * ⚠️ Callers must filter to live pointers on the current page BEFORE calling
 * this. `dedupeViewers` prefers the side panel because that is where someone
 * works, but preferring a surface here would pick a pointer that has gone
 * still and drop the one actually moving. Filtering first means every
 * candidate is live, so any survivor is correct and the choice below only ever
 * settles the rare moment when both really are moving.
 *
 * Clients from older builds report no `userId` and keep their per-connection
 * identity rather than being merged with each other on a guess.
 *
 * @param withLivePointers states already filtered to a non-null pointer on this page
 * @param localUserId the viewing user, so their own other surface is dropped
 */
export function dedupeCursors(
  withLivePointers: UserPresenceState[],
  localUserId?: string,
): UserPresenceState[] {
  const byPerson = new Map<string, UserPresenceState>();
  for (const u of withLivePointers) {
    if (localUserId && u.userId === localUserId) continue;
    const key = u.userId ?? `client:${u.clientId}`;
    if (!byPerson.has(key)) byPerson.set(key, u);
  }
  return Array.from(byPerson.values());
}
